import React, { useState, useEffect } from 'react';
import { formPaymentMethodFromApi } from '../../utils/adminPaymentAccountMethod';
import './AddAccountModal.css';

const AddAccountModal = ({ isOpen, onClose, onSave, account = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    paymentMethod: 'bank',
    accountNumber: '',
    accountHolderName: '',
    bankName: '',
    paymentProofWhatsApp: '',
    description: '',
    icon: 'wallet',
    isActive: true,
    isVisible: true,
    displayOrder: 1
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      // Handle new nested accountDetails structure or fallback to flat structure
      const accountDetails = account.accountDetails || {};
      setFormData({
        name: account.name || '',
        paymentMethod: formPaymentMethodFromApi(account.paymentMethod || 'bank'),
        accountNumber: accountDetails.accountNumber || account.accountNumber || '',
        accountHolderName: accountDetails.accountTitle || account.accountHolderName || account.accountTitle || '',
        bankName: accountDetails.bankName || account.bankName || '',
        paymentProofWhatsApp:
          accountDetails.paymentProofWhatsApp ||
          accountDetails.payment_proof_whatsapp ||
          account.paymentProofWhatsApp ||
          account.payment_proof_whatsapp ||
          '',
        description: account.description || '',
        icon: account.icon || 'wallet',
        isActive: account.isActive !== undefined ? account.isActive : true,
        isVisible: account.isVisible !== undefined ? account.isVisible : true,
        displayOrder: account.displayOrder || 1
      });
    } else {
      setFormData({
        name: '',
        paymentMethod: 'bank',
        accountNumber: '',
        accountHolderName: '',
        bankName: '',
        paymentProofWhatsApp: '',
        description: '',
        icon: 'wallet',
        isActive: true,
        isVisible: true,
        displayOrder: 1
      });
    }
    setErrors({});
  }, [account, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 1 : value);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // Auto-set icon and name for COD
      if (name === 'paymentMethod' && value === 'cod') {
        updated.icon = 'cash';
        if (!updated.name || updated.name === '') {
          updated.name = 'Cash on Delivery';
        }
      }
      
      return updated;
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }
    
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
    }
    
    // For COD, accountNumber and accountHolderName are not required
    if (formData.paymentMethod !== 'cod') {
      if (!formData.accountNumber.trim()) {
        newErrors.accountNumber = 'Account number is required';
      }
      
      if (!formData.accountHolderName.trim()) {
        newErrors.accountHolderName = 'Account holder name is required';
      }
    }
    
    if (formData.paymentMethod === 'bank' && !formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required for bank accounts';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare data for submission
      const submitData = {
        name: formData.name,
        paymentMethod: formData.paymentMethod,
        description: formData.description || '',
        icon: formData.icon,
        isActive: formData.isActive,
        isVisible: formData.isVisible,
        displayOrder: formData.displayOrder || 1
      };

      // Only include account details if not COD - use new nested structure
      if (formData.paymentMethod !== 'cod') {
        submitData.accountDetails = {
          accountNumber: formData.accountNumber,
          accountTitle: formData.accountHolderName
        };
        if (formData.paymentMethod === 'bank' || formData.bankName) {
          submitData.accountDetails.bankName = formData.bankName;
        }
        submitData.accountDetails.paymentProofWhatsApp = String(
          formData.paymentProofWhatsApp || ''
        ).trim();
      }

      await onSave(submitData);
      onClose();
    } catch (error) {
      console.error('Error saving account:', error);
      setErrors({ submit: error.message || 'Failed to save account' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const paymentMethods = [
    { value: 'cod', label: 'Cash on Delivery (COD)', icon: 'money-bill-wave' },
    { value: 'bank', label: 'Bank Account', icon: 'university' },
    { value: 'easypaisa', label: 'Easypaisa', icon: 'mobile-alt' },
    { value: 'jazzcash', label: 'JazzCash', icon: 'mobile-alt' },
    { value: 'paypal', label: 'PayPal', icon: 'paypal' },
    { value: 'stripe', label: 'Stripe', icon: 'credit-card' },
    { value: 'other', label: 'Other', icon: 'wallet' }
  ];

  const iconOptions = [
    { value: 'cash', label: 'Cash', icon: 'money-bill-wave' },
    { value: 'wallet', label: 'Wallet', icon: 'wallet' },
    { value: 'university', label: 'Bank', icon: 'university' },
    { value: 'mobile-alt', label: 'Mobile', icon: 'mobile-alt' },
    { value: 'credit-card', label: 'Card', icon: 'credit-card' },
    { value: 'paypal', label: 'PayPal', icon: 'paypal' }
  ];

  return (
    <div className="account-modal-overlay" onClick={onClose}>
      <div className="account-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="account-modal-header">
          <h2>
            <i className="fas fa-wallet"></i> {account ? 'Edit Payment Account' : 'Add Payment Account'}
          </h2>
          <button className="account-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {errors.submit && (
          <div className="account-form-error">
            <i className="fas fa-exclamation-circle"></i> {errors.submit}
          </div>
        )}

        <form className="account-form" onSubmit={handleSubmit}>
          <div className="account-form-group">
            <label htmlFor="name">
              Account Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Main Bank Account"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="account-form-group">
            <label htmlFor="paymentMethod">
              Payment Method <span className="required">*</span>
            </label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleChange}
              className={errors.paymentMethod ? 'error' : ''}
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
            {errors.paymentMethod && <span className="error-message">{errors.paymentMethod}</span>}
          </div>

          {formData.paymentMethod !== 'cod' && (
            <div className="account-form-row">
              <div className="account-form-group">
                <label htmlFor="accountNumber">
                  Account Number <span className="required">*</span>
                </label>
                <textarea
                  id="accountNumber"
                  name="accountNumber"
                  rows={3}
                  value={formData.accountNumber}
                  onChange={handleChange}
                  placeholder="Enter account number or IBAN"
                  className={`account-number-input ${errors.accountNumber ? 'error' : ''}`}
                />
                {errors.accountNumber && <span className="error-message">{errors.accountNumber}</span>}
              </div>

              <div className="account-form-group">
                <label htmlFor="accountHolderName">
                  Account Holder Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="accountHolderName"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  placeholder="Enter holder name"
                  className={errors.accountHolderName ? 'error' : ''}
                />
                {errors.accountHolderName && <span className="error-message">{errors.accountHolderName}</span>}
              </div>
            </div>
          )}

          {formData.paymentMethod !== 'cod' && (
            <div className="account-form-group">
              <label htmlFor="paymentProofWhatsApp">
                Payment proof — WhatsApp number{' '}
                <span className="optional">(Optional)</span>
              </label>
              <input
                type="text"
                id="paymentProofWhatsApp"
                name="paymentProofWhatsApp"
                value={formData.paymentProofWhatsApp}
                onChange={handleChange}
                placeholder="e.g. 923001234567 (country code, no +)"
              />
              <small className="account-field-hint">
                Checkout will open WhatsApp on this number so customers can send a payment
                screenshot. If empty, the store&apos;s default order WhatsApp is used.
              </small>
            </div>
          )}

          {formData.paymentMethod === 'bank' && (
            <div className="account-form-group">
              <label htmlFor="bankName">
                Bank Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="bankName"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="e.g., HBL, UBL, MCB"
                className={errors.bankName ? 'error' : ''}
              />
              {errors.bankName && <span className="error-message">{errors.bankName}</span>}
            </div>
          )}

          <div className="account-form-group">
            <label htmlFor="description">
              Description <span className="optional">(Optional)</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add any additional details about this account"
              rows="3"
            />
          </div>

          <div className="account-form-group">
            <label htmlFor="icon">
              Icon
            </label>
            <div className="icon-selector">
              {iconOptions.map(icon => (
                <button
                  key={icon.value}
                  type="button"
                  className={`icon-option ${formData.icon === icon.value ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, icon: icon.value }))}
                  title={icon.label}
                >
                  <i className={`fas fa-${icon.icon}`}></i>
                </button>
              ))}
            </div>
          </div>

          <div className="account-form-group">
            <label htmlFor="displayOrder">
              Display Order <span className="optional">(Optional)</span>
            </label>
            <input
              type="number"
              id="displayOrder"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleChange}
              placeholder="1"
              min="1"
            />
            <small style={{ display: 'block', marginTop: '6px', color: '#6c757d', fontSize: '0.85rem' }}>
              Lower numbers appear first in the list
            </small>
          </div>

          <div className="account-form-row">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Active Account</span>
              </label>
              <small>Account will be available for transactions</small>
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isVisible"
                  checked={formData.isVisible}
                  onChange={handleChange}
                />
                <span>Visible to Customers</span>
              </label>
              <small>Show this account on checkout page</small>
            </div>
          </div>

          <div className="account-form-actions">
            <button
              type="button"
              className="account-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="account-btn-save"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i> {account ? 'Update Account' : 'Add Account'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAccountModal;

